import { useState, useEffect } from "react";
import { 
  Wrench, Save, Building2, Euro, 
  FileText, RefreshCw, MessageCircle, Bell,
  Clock, Users, Columns, Plus, Trash2, User,
  ShieldCheck, Download, AlertTriangle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { authFetch } from "../services/authService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function GdprSection() {
  const [deleteRequest, setDeleteRequest] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch(`${API}/gdpr/delete-request`);
        if (res.ok) {
          const data = await res.json();
          setDeleteRequest(data.request);
        }
      } catch (e) { /* noop */ }
    };
    load();
  }, []);

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await authFetch(`${API}/gdpr/export`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `i_miei_dati_${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Export completato: file JSON scaricato");
      } else {
        toast.error("Errore nell'export dei dati");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    } finally {
      setExporting(false);
    }
  };

  const submitDeleteRequest = async () => {
    try {
      const res = await authFetch(`${API}/gdpr/delete-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: deleteReason }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setDeleteRequest({ status: "pending", created_at: new Date().toISOString() });
        setShowDeleteDialog(false);
      } else {
        toast.error(data.detail || "Errore nell'invio della richiesta");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    }
  };

  return (
    <div className="bg-zinc-900/50 rounded-sm border border-zinc-800 p-6" data-testid="gdpr-section">
      <h3 className="text-white font-semibold mb-1 flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-primary" />
        Privacy e Dati (GDPR)
      </h3>
      <p className="text-zinc-500 text-sm mb-4">
        Gestisci i tuoi diritti previsti dal Regolamento UE 2016/679
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-zinc-800/50 rounded-sm">
          <h4 className="text-white text-sm font-medium mb-1">Esporta i tuoi dati (Art. 20)</h4>
          <p className="text-xs text-zinc-500 mb-3">
            Scarica una copia completa di tutti i tuoi dati in formato JSON leggibile da macchina.
          </p>
          <Button size="sm" variant="outline" className="border-zinc-700" onClick={exportData}
            disabled={exporting} data-testid="gdpr-export-btn">
            <Download className="w-4 h-4 mr-1" />
            {exporting ? "Esportazione..." : "Scarica i miei dati"}
          </Button>
        </div>
        <div className="p-4 bg-zinc-800/50 rounded-sm">
          <h4 className="text-white text-sm font-medium mb-1">Cancellazione account (Art. 17)</h4>
          <p className="text-xs text-zinc-500 mb-3">
            Richiedi la cancellazione definitiva del tuo account e dei dati personali. I documenti fiscali verranno conservati per obbligo di legge.
          </p>
          {deleteRequest?.status === "pending" ? (
            <div className="text-xs text-yellow-400 flex items-center gap-2" data-testid="gdpr-delete-pending">
              <Clock className="w-4 h-4" />
              Richiesta inviata il {new Date(deleteRequest.created_at).toLocaleDateString("it-IT")} — in elaborazione (max 30 giorni)
            </div>
          ) : (
            <Button size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              onClick={() => setShowDeleteDialog(true)} data-testid="gdpr-delete-request-btn">
              <Trash2 className="w-4 h-4 mr-1" />
              Richiedi cancellazione
            </Button>
          )}
        </div>
      </div>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Richiesta Cancellazione Account
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-zinc-400">
              La richiesta verrà inviata all'amministratore ed elaborata entro 30 giorni. La cancellazione è <strong className="text-red-400">definitiva e irreversibile</strong>: account, clienti, preventivi, appuntamenti e dati personali verranno eliminati. Fatture e documenti fiscali verranno conservati in forma anonimizzata per obbligo di legge (10 anni).
            </p>
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Motivo (facoltativo)</label>
              <Input
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Es: cessazione attività"
                data-testid="gdpr-delete-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-zinc-700" onClick={() => setShowDeleteDialog(false)}>Annulla</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={submitDeleteRequest} data-testid="gdpr-delete-confirm-btn">
              Invia Richiesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const DAYS = [
  { key: "lunedi", label: "Lunedì" },
  { key: "martedi", label: "Martedì" },
  { key: "mercoledi", label: "Mercoledì" },
  { key: "giovedi", label: "Giovedì" },
  { key: "venerdi", label: "Venerdì" },
  { key: "sabato", label: "Sabato" },
  { key: "domenica", label: "Domenica" },
];

export default function Settings({ user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // Costi di default per preventivi
    default_labor_cost: "",
    default_waste_disposal_cost: "",
    default_consumables_cost: "",
    // Dati officina per stampe PDF
    print_company_name: "",
    print_address: "",
    print_city: "",
    print_province: "",
    print_cap: "",
    print_phone: "",
    print_email: "",
    print_vat_number: "",
    print_iban: "",
    // Dati e-Fattura
    print_fiscal_code: "",
    print_pec: "",
    print_sdi_code: "",
    // Messaggio promemoria WhatsApp
    reminder_message_template: "",
    // Configurazioni Agenda
    opening_hours: [],
    operators: [],
    num_bridges: 1,
  });

  // Dialog per aggiungere operatore
  const [showOperatorDialog, setShowOperatorDialog] = useState(false);
  const [newOperator, setNewOperator] = useState({ name: "", phone: "", specialization: "" });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await authFetch(`${API}/officina/settings`, { });
      if (res.ok) {
        const data = await res.json();
        setSettings({
          default_labor_cost: data.default_labor_cost || "",
          default_waste_disposal_cost: data.default_waste_disposal_cost || "",
          default_consumables_cost: data.default_consumables_cost || "",
          print_company_name: data.print_company_name || "",
          print_address: data.print_address || "",
          print_city: data.print_city || "",
          print_province: data.print_province || "",
          print_cap: data.print_cap || "",
          print_phone: data.print_phone || "",
          print_email: data.print_email || "",
          print_vat_number: data.print_vat_number || "",
          print_iban: data.print_iban || "",
          print_fiscal_code: data.print_fiscal_code || "",
          print_pec: data.print_pec || "",
          print_sdi_code: data.print_sdi_code || "",
          reminder_message_template: data.reminder_message_template || "",
          opening_hours: data.opening_hours || [],
          operators: data.operators || [],
          num_bridges: data.num_bridges || 1,
        });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/officina/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          default_labor_cost: parseFloat(settings.default_labor_cost) || 0,
          default_waste_disposal_cost: parseFloat(settings.default_waste_disposal_cost) || 0,
          default_consumables_cost: parseFloat(settings.default_consumables_cost) || 0,
          print_company_name: settings.print_company_name,
          print_address: settings.print_address,
          print_city: settings.print_city,
          print_province: settings.print_province,
          print_cap: settings.print_cap,
          print_phone: settings.print_phone,
          print_email: settings.print_email,
          print_vat_number: settings.print_vat_number,
          print_iban: settings.print_iban,
          print_fiscal_code: settings.print_fiscal_code,
          print_pec: settings.print_pec,
          print_sdi_code: settings.print_sdi_code,
          reminder_message_template: settings.reminder_message_template,
          opening_hours: settings.opening_hours,
          operators: settings.operators,
          num_bridges: parseInt(settings.num_bridges) || 1,
        })
      });
      
      if (res.ok) {
        toast.success("Configurazioni salvate con successo");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nel salvataggio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setSaving(false);
    }
  };

  const handleCopyFromProfile = () => {
    setSettings(prev => ({
      ...prev,
      print_company_name: user?.company_name || "",
      print_address: user?.address || "",
      print_city: user?.city || "",
      print_province: user?.province || "",
      print_cap: user?.zip_code || "",
      print_phone: user?.phone || "",
      print_email: user?.email || "",
      print_vat_number: user?.vat_number || "",
    }));
    toast.success("Dati copiati dal profilo");
  };

  // Funzioni per orari apertura
  const updateOpeningHour = (dayKey, field, value) => {
    setSettings(prev => ({
      ...prev,
      opening_hours: prev.opening_hours.map(oh => 
        oh.day === dayKey ? { ...oh, [field]: value } : oh
      )
    }));
  };

  // Funzioni per operatori
  const addOperator = () => {
    if (!newOperator.name.trim()) {
      toast.error("Inserisci il nome dell'operatore");
      return;
    }
    
    const operator = {
      id: `op_${Date.now()}`,
      name: newOperator.name.trim(),
      phone: newOperator.phone.trim(),
      specialization: newOperator.specialization.trim(),
      active: true
    };
    
    setSettings(prev => ({
      ...prev,
      operators: [...prev.operators, operator]
    }));
    
    setNewOperator({ name: "", phone: "", specialization: "" });
    setShowOperatorDialog(false);
    toast.success("Operatore aggiunto");
  };

  const removeOperator = (operatorId) => {
    setSettings(prev => ({
      ...prev,
      operators: prev.operators.filter(op => op.id !== operatorId)
    }));
    toast.success("Operatore rimosso");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Wrench className="w-7 h-7 text-primary" />
            Configurazioni
          </h1>
          <p className="text-zinc-400 mt-1">
            Imposta i valori di default per preventivi, stampe PDF e agenda lavori
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="btn-primary">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Salvataggio..." : "Salva Configurazioni"}
        </Button>
      </div>

      {/* Configurazioni Agenda Lavori */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-6">
          <Clock className="w-5 h-5 text-primary" />
          Configurazioni Agenda Lavori
        </h2>
        <p className="text-zinc-400 text-sm mb-6">
          Imposta gli orari di apertura, il numero di ponti e gli operatori addetti alle riparazioni.
          Questi dati vengono usati per la schedulazione automatica degli ordini di lavoro.
        </p>

        {/* Orari Apertura */}
        <div className="mb-8">
          <h3 className="text-md font-semibold text-zinc-300 mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Orari di Apertura
          </h3>
          <div className="space-y-3">
            {settings.opening_hours.map((oh) => {
              const dayInfo = DAYS.find(d => d.key === oh.day);
              return (
                <div key={oh.day} className="flex items-center gap-4 p-3 bg-zinc-800/50 rounded-sm">
                  <label className="flex items-center gap-2 w-32">
                    <input
                      type="checkbox"
                      checked={oh.is_open}
                      onChange={(e) => updateOpeningHour(oh.day, "is_open", e.target.checked)}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className={`text-sm ${oh.is_open ? 'text-white' : 'text-zinc-500'}`}>
                      {dayInfo?.label || oh.day}
                    </span>
                  </label>
                  
                  {oh.is_open && (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-500">Dalle</span>
                        <Input
                          type="time"
                          value={oh.open_time || "08:00"}
                          onChange={(e) => updateOpeningHour(oh.day, "open_time", e.target.value)}
                          className="w-28 text-sm"
                        />
                        <span className="text-xs text-zinc-500">alle</span>
                        <Input
                          type="time"
                          value={oh.close_time || "18:00"}
                          onChange={(e) => updateOpeningHour(oh.day, "close_time", e.target.value)}
                          className="w-28 text-sm"
                        />
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        <span className="text-xs text-zinc-500">Pausa</span>
                        <Input
                          type="time"
                          value={oh.break_start || ""}
                          onChange={(e) => updateOpeningHour(oh.day, "break_start", e.target.value || null)}
                          className="w-28 text-sm"
                          placeholder="--:--"
                        />
                        <span className="text-xs text-zinc-500">-</span>
                        <Input
                          type="time"
                          value={oh.break_end || ""}
                          onChange={(e) => updateOpeningHour(oh.day, "break_end", e.target.value || null)}
                          className="w-28 text-sm"
                          placeholder="--:--"
                        />
                      </div>
                    </>
                  )}
                  
                  {!oh.is_open && (
                    <span className="text-zinc-500 text-sm italic">Chiuso</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Ponti e Operatori */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Numero Ponti */}
          <div>
            <h3 className="text-md font-semibold text-zinc-300 mb-4 flex items-center gap-2">
              <Columns className="w-4 h-4" />
              Ponti Sollevatori
            </h3>
            <div className="p-4 bg-zinc-800/50 rounded-sm">
              <label className="text-sm text-zinc-400 mb-2 block">
                Numero di ponti disponibili per le riparazioni
              </label>
              <Input
                type="number"
                min="1"
                max="20"
                value={settings.num_bridges}
                onChange={(e) => setSettings(prev => ({ ...prev, num_bridges: parseInt(e.target.value) || 1 }))}
                className="w-24"
                data-testid="input-num-bridges"
              />
              <p className="text-xs text-zinc-500 mt-2">
                Il numero di ponti determina quanti lavori possono essere eseguiti in parallelo.
              </p>
            </div>
          </div>

          {/* Operatori */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-md font-semibold text-zinc-300 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Operatori Riparazione
              </h3>
              <Button 
                size="sm" 
                onClick={() => setShowOperatorDialog(true)}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-1" />
                Aggiungi
              </Button>
            </div>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {settings.operators.length === 0 ? (
                <div className="p-4 bg-zinc-800/50 rounded-sm text-center">
                  <User className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                  <p className="text-zinc-500 text-sm">Nessun operatore registrato</p>
                  <p className="text-zinc-600 text-xs mt-1">
                    Aggiungi gli operatori per assegnarli agli ordini di lavoro
                  </p>
                </div>
              ) : (
                settings.operators.map((op) => (
                  <div key={op.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-sm">
                    <div>
                      <p className="text-white font-medium">{op.name}</p>
                      <div className="flex items-center gap-3 text-xs text-zinc-500">
                        {op.phone && <span>{op.phone}</span>}
                        {op.specialization && (
                          <span className="px-2 py-0.5 bg-primary/20 text-primary rounded">
                            {op.specialization}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeOperator(op.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Costi Default Preventivi */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-6">
            <Euro className="w-5 h-5 text-primary" />
            Costi Default Preventivi
          </h2>
          <p className="text-zinc-400 text-sm mb-6">
            Questi valori verranno inseriti automaticamente nei nuovi preventivi.
            Potrai comunque modificarli manualmente in ogni preventivo.
          </p>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">
                Costo Manodopera (€/ora)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={settings.default_labor_cost}
                onChange={(e) => setSettings({ ...settings, default_labor_cost: e.target.value })}
                placeholder="Es: 45.00"
                className="w-full"
                data-testid="input-labor-cost"
              />
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">
                Costo Smaltimento Rifiuti (€)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={settings.default_waste_disposal_cost}
                onChange={(e) => setSettings({ ...settings, default_waste_disposal_cost: e.target.value })}
                placeholder="Es: 15.00"
                className="w-full"
                data-testid="input-waste-cost"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Costo forfettario per smaltimento olii, filtri, ecc.
              </p>
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">
                Costo Materiali di Consumo (€)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={settings.default_consumables_cost}
                onChange={(e) => setSettings({ ...settings, default_consumables_cost: e.target.value })}
                placeholder="Es: 10.00"
                className="w-full"
                data-testid="input-consumables-cost"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Costo forfettario per materiali di consumo (guanti, panni, sgrassanti, ecc.)
              </p>
            </div>
          </div>
        </div>

        {/* Dati Officina per Stampe PDF */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Dati Officina per Stampe PDF
            </h2>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopyFromProfile}
              className="text-xs"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Copia da Profilo
            </Button>
          </div>
          <p className="text-zinc-400 text-sm mb-6">
            Questi dati verranno utilizzati nell'intestazione dei documenti PDF
            (accettazioni, preventivi, fatture).
          </p>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">
                Ragione Sociale
              </label>
              <Input
                type="text"
                value={settings.print_company_name}
                onChange={(e) => setSettings({ ...settings, print_company_name: e.target.value })}
                placeholder="Es: Autofficina Rossi S.r.l."
                className="w-full"
                data-testid="input-print-company"
              />
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">
                Indirizzo
              </label>
              <Input
                type="text"
                value={settings.print_address}
                onChange={(e) => setSettings({ ...settings, print_address: e.target.value })}
                placeholder="Es: Via Roma 123"
                className="w-full"
                data-testid="input-print-address"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Città</label>
                <Input
                  type="text"
                  value={settings.print_city}
                  onChange={(e) => setSettings({ ...settings, print_city: e.target.value })}
                  placeholder="Milano"
                  className="w-full"
                  data-testid="input-print-city"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Provincia</label>
                <Input
                  type="text"
                  maxLength={2}
                  value={settings.print_province}
                  onChange={(e) => setSettings({ ...settings, print_province: e.target.value.toUpperCase() })}
                  placeholder="MI"
                  className="w-full uppercase"
                  data-testid="input-print-province"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">CAP</label>
                <Input
                  type="text"
                  maxLength={5}
                  value={settings.print_cap}
                  onChange={(e) => setSettings({ ...settings, print_cap: e.target.value.replace(/\D/g, '') })}
                  placeholder="20100"
                  className="w-full"
                  data-testid="input-print-cap"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Telefono</label>
                <Input
                  type="tel"
                  value={settings.print_phone}
                  onChange={(e) => setSettings({ ...settings, print_phone: e.target.value })}
                  placeholder="+39 02 1234567"
                  className="w-full"
                  data-testid="input-print-phone"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Email</label>
                <Input
                  type="email"
                  value={settings.print_email}
                  onChange={(e) => setSettings({ ...settings, print_email: e.target.value })}
                  placeholder="info@officina.it"
                  className="w-full"
                  data-testid="input-print-email"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-1 block">
                Partita IVA
              </label>
              <Input
                type="text"
                maxLength={11}
                value={settings.print_vat_number}
                onChange={(e) => setSettings({ ...settings, print_vat_number: e.target.value.replace(/\D/g, '') })}
                placeholder="12345678901"
                className="w-full"
                data-testid="input-print-vat"
              />
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-1 block">
                IBAN
              </label>
              <Input
                type="text"
                maxLength={34}
                value={settings.print_iban}
                onChange={(e) => setSettings({ ...settings, print_iban: e.target.value.toUpperCase().replace(/\s/g, '') })}
                placeholder="IT60X0542811101000000123456"
                className="w-full uppercase"
                data-testid="input-print-iban"
              />
              <p className="text-xs text-zinc-500 mt-1">
                L'IBAN verrà visualizzato nei PDF per i pagamenti
              </p>
            </div>

            {/* Dati e-Fattura */}
            <div className="border-t border-zinc-700 pt-4 mt-4">
              <h3 className="text-sm font-semibold text-primary mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Dati per e-Fattura (FatturaPA)
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">
                    Codice Fiscale Azienda
                  </label>
                  <Input
                    type="text"
                    maxLength={16}
                    value={settings.print_fiscal_code}
                    onChange={(e) => setSettings({ ...settings, print_fiscal_code: e.target.value.toUpperCase() })}
                    placeholder="RSSMRA80A01H501Z o 12345678901"
                    className="w-full uppercase"
                    data-testid="input-print-fiscal-code"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Per società coincide con la P.IVA, per ditte individuali è il CF del titolare
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">
                      PEC (Posta Certificata)
                    </label>
                    <Input
                      type="email"
                      value={settings.print_pec}
                      onChange={(e) => setSettings({ ...settings, print_pec: e.target.value.toLowerCase() })}
                      placeholder="azienda@pec.it"
                      className="w-full"
                      data-testid="input-print-pec"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">
                      Codice SDI
                    </label>
                    <Input
                      type="text"
                      maxLength={7}
                      value={settings.print_sdi_code}
                      onChange={(e) => setSettings({ ...settings, print_sdi_code: e.target.value.toUpperCase() })}
                      placeholder="0000000"
                      className="w-full uppercase"
                      data-testid="input-print-sdi"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Codice Destinatario SdI (7 caratteri)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messaggio Promemoria WhatsApp */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-6">
          <MessageCircle className="w-5 h-5 text-green-500" />
          Messaggio Promemoria WhatsApp
        </h2>
        <p className="text-zinc-400 text-sm mb-4">
          Personalizza il messaggio che verrà inviato come promemoria ai clienti il giorno prima dell'appuntamento.
          Lascia vuoto per usare il messaggio standard.
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">
              Template Messaggio
            </label>
            <textarea
              value={settings.reminder_message_template}
              onChange={(e) => setSettings({ ...settings, reminder_message_template: e.target.value })}
              placeholder={`Gentile {nome},\n\nLe ricordiamo il Suo appuntamento per DOMANI:\n\n📅 Data: {data}\n🕐 Ora: {ora}\n🚗 Veicolo: {veicolo} - {targa}\n🔧 Servizio: {servizio}\n\nLa aspettiamo!\n\n{officina}`}
              className="w-full h-48 px-4 py-3 bg-white border border-zinc-300 rounded-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all resize-none font-mono text-sm"
              data-testid="input-reminder-template"
            />
            <p className="text-xs text-zinc-500 mt-2">
              Variabili disponibili: <code className="text-primary">{"{nome}"}</code>, <code className="text-primary">{"{data}"}</code>, <code className="text-primary">{"{ora}"}</code>, <code className="text-primary">{"{veicolo}"}</code>, <code className="text-primary">{"{targa}"}</code>, <code className="text-primary">{"{servizio}"}</code>, <code className="text-primary">{"{note}"}</code>, <code className="text-primary">{"{officina}"}</code>
            </p>
          </div>
          
          <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-4">
            <h4 className="text-green-400 font-semibold mb-2 flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Come funziona
            </h4>
            <ul className="text-zinc-400 text-sm space-y-1">
              <li>• Vai nella pagina <strong>Appuntamenti</strong> per vedere la sezione "Promemoria Domani"</li>
              <li>• Clicca "Copia e Invia Tutti" per copiare il messaggio negli appunti</li>
              <li>• Vedrai la lista dei numeri WhatsApp da contattare</li>
              <li>• Incolla il messaggio in ogni chat WhatsApp</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Privacy e Dati (GDPR) */}
      <GdprSection />

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-4">
        <h3 className="text-blue-400 font-semibold mb-2 flex items-center gap-2">
          <Building2 className="w-4 h-4" />
          Informazioni
        </h3>
        <ul className="text-zinc-400 text-sm space-y-1">
          <li>• I costi di default verranno pre-compilati nei nuovi preventivi</li>
          <li>• Puoi sempre modificare i valori manualmente in ogni singolo preventivo</li>
          <li>• I dati per le stampe PDF sovrascrivono i dati del profilo nelle intestazioni</li>
          <li>• Se lasci vuoti i campi per le stampe, verranno usati i dati del profilo</li>
          <li>• Gli orari di apertura e i ponti vengono usati per la schedulazione automatica degli ordini di lavoro</li>
        </ul>
      </div>

      {/* Dialog Aggiungi Operatore */}
      <Dialog open={showOperatorDialog} onOpenChange={setShowOperatorDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Aggiungi Operatore
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Nome *</label>
              <Input
                type="text"
                value={newOperator.name}
                onChange={(e) => setNewOperator(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Es: Mario Rossi"
                className="w-full"
                data-testid="input-operator-name"
              />
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Telefono (per WhatsApp)</label>
              <Input
                type="tel"
                value={newOperator.phone}
                onChange={(e) => setNewOperator(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="Es: +39 333 1234567"
                className="w-full"
                data-testid="input-operator-phone"
              />
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Specializzazione</label>
              <Input
                type="text"
                value={newOperator.specialization}
                onChange={(e) => setNewOperator(prev => ({ ...prev, specialization: e.target.value }))}
                placeholder="Es: Meccanico, Elettrauto, Gommista"
                className="w-full"
                data-testid="input-operator-spec"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOperatorDialog(false)}>
              Annulla
            </Button>
            <Button onClick={addOperator} className="btn-primary">
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
